import {
  IQformListInput,
  InputHelperService,
  QformInputBase,
  QformItemSelectorInput,
  ListOption,
} from 'qflow-form-renderer';
import { FlowDataOutputDto } from '../../core/remote-clients/remote-client';
import {
  InputDependency,
  OutputDependency,
} from './domain-dependency-parser.service';

export interface DataDependencyCallbacks {
  getInputs: () => QformInputBase[];
  fetchDataSourceOptions: (
    data: FlowDataOutputDto
  ) => Promise<ListOption[] | null>;
  initializeListInput: (input: IQformListInput, options: ListOption[]) => void;
}

export class DataDependencyService {
  private _inputDependencyMap = new Map<string, InputDependency[]>();
  private _sourceToTargetsMap = new Map<string, string[]>();
  private _outputDependencyMap = new Map<string, OutputDependency[]>();
  private _lastKnownSourceValues = new Map<string, string | null | undefined>();
  private _currentDataList: FlowDataOutputDto[] = [];

  constructor(private callbacks: DataDependencyCallbacks) {}

  initDependencies(
    inputMap: Map<string, InputDependency[]>,
    sourceToTargets: Map<string, string[]>,
    outputBySourceControl: Map<string, OutputDependency[]>
  ): void {
    this._inputDependencyMap = inputMap;
    this._sourceToTargetsMap = sourceToTargets;
    this._outputDependencyMap = outputBySourceControl;
    this._lastKnownSourceValues.clear();
  }

  setCurrentDataList(dataList: FlowDataOutputDto[]): void {
    this._currentDataList = dataList;
  }

  hasOutputDependencies(): boolean {
    return this._outputDependencyMap.size > 0;
  }

  hasSourceToTargets(): boolean {
    return this._sourceToTargetsMap.size > 0;
  }

  hasOutputDependency(targetQflowId: string): boolean {
    return this._outputDependencyMap.has(targetQflowId);
  }

  initializeSourceValues(): void {
    const inputs = this.callbacks.getInputs();
    for (const [sourceId] of this._sourceToTargetsMap) {
      const input = inputs.find(i => i.qflowId === sourceId);
      this._lastKnownSourceValues.set(sourceId, input?.value ?? null);
    }
  }

  hasInputDependency(qflowId: string): boolean {
    return this._inputDependencyMap.has(qflowId);
  }

  getInputDependencies(qflowId: string): InputDependency[] {
    return this._inputDependencyMap.get(qflowId) ?? [];
  }

  handleCascadeChange(): Promise<string[]> {
    const inputs = this.callbacks.getInputs();
    const changedSourceIds: string[] = [];

    for (const [sourceId] of this._sourceToTargetsMap) {
      const sourceInput = inputs.find(i => i.qflowId === sourceId);
      const currentValue = sourceInput?.value ?? null;
      const cachedValue = this._lastKnownSourceValues.get(sourceId) ?? null;

      if (currentValue !== cachedValue) {
        changedSourceIds.push(sourceId);
        this._lastKnownSourceValues.set(sourceId, currentValue);
      }
    }

    if (changedSourceIds.length === 0) {
      return Promise.resolve([]);
    }

    return this.applyCascadeForSources(changedSourceIds);
  }

  applyOutgoingDataDependencies(exemptTargetIds: string[] = []): void {
    if (this._outputDependencyMap.size === 0) {
      return;
    }

    const inputs = this.callbacks.getInputs();

    for (const [sourceId, deps] of this._outputDependencyMap) {
      const sourceInput = inputs.find(i => i.qflowId === sourceId);
      if (!sourceInput) {
        continue;
      }

      const bInputDeps = this._inputDependencyMap.get(sourceId) || [];
      const rawVal = sourceInput.value;
      const hasValue = rawVal != null && String(rawVal).trim() !== '';

      if (!hasValue) {
        this.clearOutgoingTargets(
          inputs,
          deps,
          bInputDeps,
          exemptTargetIds,
          true
        );

        continue;
      }

      const cols = this.resolveSourceColumnValues(sourceInput);
      if (cols === undefined || cols.length === 0) {
        this.clearOutgoingTargets(
          inputs,
          deps,
          bInputDeps,
          exemptTargetIds,
          false
        );

        continue;
      }

      for (const d of deps) {
        const isSkip =
          this.isCyclicTarget(bInputDeps, d.targetQflowId) ||
          this.isIncomingDependencyTarget(d.targetQflowId) ||
          this.isDependencySourceControl(d.targetQflowId);

        if (isSkip) {
          continue;
        }

        const target = inputs.find(i => i.qflowId === d.targetQflowId);
        if (!target) {
          continue;
        }

        const cell =
          d.columnIndex >= 0 && d.columnIndex < cols.length
            ? cols[d.columnIndex]
            : undefined;
        const toSet =
          cell !== undefined && cell !== null && cell !== '' ? cell : null;
        target.setControlValue(toSet);
      }
    }
  }

  private clearOutgoingTargets(
    inputs: QformInputBase[],
    deps: OutputDependency[],
    bInputDeps: InputDependency[],
    exemptTargetIds: string[],
    skipExemptTargets: boolean
  ): void {
    for (const d of deps) {
      const isSkip =
        this.isCyclicTarget(bInputDeps, d.targetQflowId) ||
        (skipExemptTargets && exemptTargetIds.includes(d.targetQflowId));

      if (isSkip) {
        continue;
      }

      inputs.find(i => i.qflowId === d.targetQflowId)?.setControlValue(null);
    }
  }

  syncAllKnownSourceValues(): void {
    const inputs = this.callbacks.getInputs();

    for (const sourceId of this._sourceToTargetsMap.keys()) {
      const input = inputs.find(i => i.qflowId === sourceId);
      this._lastKnownSourceValues.set(sourceId, input?.value ?? null);
    }
  }

  private async applyCascadeForSources(
    changedSourceIds: string[]
  ): Promise<string[]> {
    const inputs = this.callbacks.getInputs();
    const fetchPromises: Promise<void>[] = [];

    for (const sourceId of changedSourceIds) {
      const targetIds = this._sourceToTargetsMap.get(sourceId) ?? [];

      for (const targetId of targetIds) {
        const targetData = this._currentDataList.find(d => d.id === targetId);
        if (!targetData) {
          continue;
        }

        const targetInput = inputs.find(i => i.qflowId === targetId);
        this.clearCascadeTargetInput(targetInput);
        this.syncKnownValue(targetId, targetInput);

        fetchPromises.push(
          this.fetchAndInitTargetOptions(targetData, targetInput).catch(
            () => {}
          )
        );
      }
    }

    await Promise.all(fetchPromises);
    return changedSourceIds;
  }

  private async fetchAndInitTargetOptions(
    targetData: FlowDataOutputDto,
    targetInput: QformInputBase | undefined
  ): Promise<void> {
    const options = await this.callbacks.fetchDataSourceOptions(targetData);
    if (options && targetInput && InputHelperService.isListInput(targetInput)) {
      this.callbacks.initializeListInput(
        targetInput as IQformListInput,
        options
      );
      (targetInput as any).setControlValue(null);
      this.syncKnownValue(targetData.id ?? '', targetInput);
    }
  }

  private isIncomingDependencyTarget(targetQflowId: string): boolean {
    if (!targetQflowId) {
      return false;
    }

    return this._inputDependencyMap.has(targetQflowId);
  }

  private isDependencySourceControl(targetQflowId: string): boolean {
    if (!targetQflowId) {
      return false;
    }

    return (
      this._sourceToTargetsMap.has(targetQflowId) ||
      this._outputDependencyMap.has(targetQflowId)
    );
  }

  private isCyclicTarget(
    bInputDeps: InputDependency[],
    targetQflowId: string
  ): boolean {
    if (!targetQflowId) return false;
    const targetLower = targetQflowId.toLowerCase().trim();
    return bInputDeps.some(
      dep =>
        dep.sourceQflowId &&
        dep.sourceQflowId.toLowerCase().trim() === targetLower
    );
  }

  private resolveSourceColumnValues(
    sourceInput: QformInputBase
  ): string[] | undefined {
    if (sourceInput instanceof QformItemSelectorInput) {
      return sourceInput.selectedColumnValues;
    }

    if (InputHelperService.isListInput(sourceInput)) {
      const listInput = sourceInput as IQformListInput;
      const val = String(sourceInput.value ?? '');
      const options = Array.isArray(listInput.listOptions)
        ? listInput.listOptions
        : [];
      return options.find(o => o.key === val)?.columnValues;
    }

    return undefined;
  }

  private syncKnownValue(id: string, input: QformInputBase | undefined): void {
    if (!this._sourceToTargetsMap.has(id)) {
      return;
    }
    this._lastKnownSourceValues.set(id, input?.value ?? null);
  }

  private clearCascadeTargetInput(input: QformInputBase | undefined): void {
    if (!input) {
      return;
    }

    if (InputHelperService.isListInput(input)) {
      this.callbacks.initializeListInput(input as IQformListInput, []);
    }

    input.setControlValue(null);
  }
}
